import React, { useState } from 'react';
import { useQuery, useMutation, useAction, usePaginatedQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id, Doc } from '../../../../convex/_generated/dataModel';
import { SimpleFilterBar } from './SimpleFilterBar';
import { GridView, MasonryView, ListView } from './ReferenceViews';
import { SelectionActionBar } from './SelectionActionBar';
import { ReferenceSelectionProvider } from '../../../shared/contexts/ReferenceSelectionContext';
import { useSearch } from '../../../shared/contexts/SearchContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AIPromptGeneratorModal, type PromptType } from '../../saved-prompts/components/AIPromptGeneratorModal';
import { collectReferenceData } from '../../saved-prompts/components/aiPromptGenerator';
import { useTagUrlSync } from '../hooks/useTagUrlSync';
import { InfiniteScrollSentinel } from '@/shared/ui/components';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

interface UnifiedSearchPageProps {
  className?: string;
  onEditReference?: (reference: ReferenceDoc) => void;
}

export function UnifiedSearchPage({ className = '', onEditReference }: UnifiedSearchPageProps) {
  const navigate = useNavigate();
  const [deletingReference, setDeletingReference] = useState<ReferenceDoc | null>(null);
  
  // AI Prompt Generator state
  const [aiPromptReference, setAiPromptReference] = useState<ReferenceDoc | null>(null);
  const [isAIPromptModalOpen, setIsAIPromptModalOpen] = useState(false);
  
  // Use enhanced search context with both legacy and new filter systems
  const { 
    filters, 
    setFilters, 
    viewMode, 
    filterState, 
    filterDispatch,
    hasActiveFilters: hasNewFilters 
  } = useSearch();
  
  // Initialize URL-filter synchronization
  useTagUrlSync();

  // Helper function to generate URLs with preserved parameters
  const generateReferenceUrl = (referenceId: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    const queryString = currentParams.toString();
    return `/reference/${referenceId}${queryString ? `?${queryString}` : ''}`;
  };

  // Delete mutation
  const deleteReference = useMutation(api.references.deleteReference);

  // Convex action for AI prompt generation
  const generateAIPrompt = useAction(api.aiPromptGenerator.generateAIPrompt);

  // Fetch available tags and apps for search suggestions
  const allTags = useQuery(api.tags.getAllUserTags) || [];
  
  // Fetch tag cloud data for usage counts
  const tagCloudData = useQuery(api.tags.getTagCloudData);

  // Search references based on current filters (legacy system)
  const hasActiveFilters = filters.searchText || filters.tags.length > 0 || filters.apps.length > 0;
  const searchResults = useQuery(
    api.references.searchReferences,
    hasActiveFilters
      ? {
          searchQuery: filters.searchText || '',
          tagIds: filters.tags.filter((t: any) => !t.negative).map((t: any) => t.id),
          appIds: filters.apps.filter((a: any) => !a.negative).map((a: any) => a.id),
        }
      : 'skip'
  );

  // Advanced filtering: paginated path (no search term)
  const hasSearchTerm = !!filterState.searchTerm?.trim();
  const advancedPaginated = usePaginatedQuery(
    api.references.searchReferencesAdvanced,
    (hasNewFilters && !hasSearchTerm)
      ? {
          searchTerm: '',
          tags: filterState.tags,
          globalLogic: filterState.globalLogic,
        }
      : 'skip',
    { initialNumItems: 40 }
  );

  // Advanced filtering: collected path (with search term)
  const advancedSearchCollected = useQuery(
    api.references.searchReferencesAdvanced,
    (hasNewFilters && hasSearchTerm)
      ? {
          searchTerm: filterState.searchTerm || '',
          tags: filterState.tags,
          globalLogic: filterState.globalLogic,
        }
      : 'skip'
  );

  // Get all references when no filters are applied (paginated)
  const allReferencesPaginated = usePaginatedQuery(
    api.references.getReferences,
    (!hasActiveFilters && !hasNewFilters) ? {} : 'skip',
    { initialNumItems: 40 }
  );

  // Determine pagination state and which results to show
  const isPaginated = (!hasActiveFilters && !hasNewFilters)
    || (hasNewFilters && !hasSearchTerm);

  const paginatedResult = (!hasActiveFilters && !hasNewFilters)
    ? allReferencesPaginated
    : (hasNewFilters && !hasSearchTerm)
      ? advancedPaginated
      : null;

  const references: ReferenceDoc[] = isPaginated
    ? (paginatedResult?.results || []) as ReferenceDoc[]
    : hasNewFilters
      ? ((advancedSearchCollected as any)?.page || []) as ReferenceDoc[]
      : (searchResults || []) as ReferenceDoc[];

  // Ensure references is always an array
  const safeReferences = Array.isArray(references) ? references : [];

  // Apply legacy client-side negative filtering only for legacy system
  const legacyFilteredReferences = hasNewFilters ? safeReferences : safeReferences.filter(ref => {
    // Check negative tag filters
    const negativeTags = filters.tags.filter((t: any) => t.negative);
    if (negativeTags.length > 0) {
      const hasNegativeTag = negativeTags.some((negTag: any) => 
        ref.tags.some(tag => tag._id === negTag.id)
      );
      if (hasNegativeTag) return false;
    }

    // Check negative app filters
    const negativeApps = filters.apps.filter((a: any) => a.negative);
    if (negativeApps.length > 0) {
      const hasNegativeApp = negativeApps.some((negApp: any) => ref.appId === negApp.id);
      if (hasNegativeApp) return false;
    }

    return true;
  });

  // The final filtered references are now handled server-side for advanced filtering
  const finalFilteredReferences = legacyFilteredReferences;

  // Prepare available tags for SimpleFilterBar with usage counts
  const availableTagsForFilter = tagCloudData?.tags.map(tag => ({
    name: tag.name,
    category: undefined, // We can add categories later
    count: tag.count
  })) || [];

  // Debug: Log tag data to verify counts are included
  console.log('Tag cloud data:', tagCloudData?.tags?.slice(0, 5));
  console.log('Available tags for filter:', availableTagsForFilter.slice(0, 5));

  // Handle legacy tag click (using existing TagChip)
  const handleTagClick = (tagId: string) => {
    const tag = allTags.find(t => t._id === tagId);
    if (!tag) return;

    const existingTagIndex = filters.tags.findIndex((t: any) => t.id === tagId);
    if (existingTagIndex === -1) {
      // Add tag filter
      const newRawQuery = filters.rawQuery ? `${filters.rawQuery} tag:${tag.name}` : `tag:${tag.name}`;
      setFilters({
        ...filters,
        tags: [...filters.tags, { id: tagId as Id<'tags'>, name: tag.name, negative: false }],
        rawQuery: newRawQuery
      });
    } else {
      // Remove tag filter (toggle behavior)
      const newTags = filters.tags.filter((t: any) => t.id !== tagId);
      // Update raw query by removing the tag
      const tagPattern = new RegExp(`\\s*-?tag:${tag.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gi');
      const newRawQuery = filters.rawQuery.replace(tagPattern, ' ').trim();
      setFilters({
        ...filters,
        tags: newTags,
        rawQuery: newRawQuery
      });
    }
  };

  // Handle new tag selection from SimpleFilterBar
  const handleNewTagSelect = (tagName: string, category?: string) => {
    // This will be handled automatically by SimpleFilterBar through the context
    console.log('Tag selected from SimpleFilterBar:', tagName, category);
  };

  const handleDelete = (reference: ReferenceDoc) => {
    setDeletingReference(reference);
  };

  const confirmDelete = async () => {
    if (!deletingReference) return;
    
    try {
      await deleteReference({ id: deletingReference._id });
      toast.success("Reference deleted successfully!");
      setDeletingReference(null);
    } catch (error) {
      toast.error("Failed to delete reference.");
      console.error(error);
    }
  };

  const cancelDelete = () => {
    setDeletingReference(null);
  };

  // Handle AI prompt click - opens the modal
  const handleAIPromptClick = (reference: ReferenceDoc) => {
    setAiPromptReference(reference);
    setIsAIPromptModalOpen(true);
  };

  // Handle AI prompt generation - now uses real OpenAI integration
  const handleGenerateAIPrompt = async (
    reference: ReferenceDoc, 
    promptType: PromptType, 
    customInstructions?: string
  ): Promise<string> => {
    try {
      // Collect and process reference data
      const referenceData = await collectReferenceData(reference);
      
      // Call the Convex action for OpenAI integration
      const result = await generateAIPrompt({
        referenceData,
        promptType,
        customInstructions
      });

      return result;
    } catch (error) {
      console.error('Error generating AI prompt:', error);
      throw error instanceof Error ? error : new Error('Failed to generate AI prompt');
    }
  };

  // Helper function to safely extract hostname from a URL
  const getHostnameFromUrl = (url: string): string | null => {
    try {
      // Try to construct a URL object
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      // If URL is invalid, try to extract domain manually
      try {
        // Remove protocol if present
        const cleanUrl = url.replace(/^https?:\/\//, '');
        // Extract domain part (before first slash or query)
        const domain = cleanUrl.split(/[/?#]/)[0];
        // Basic validation - should contain at least one dot
        if (domain && domain.includes('.')) {
          return domain;
        }
      } catch {
        // If all fails, return null
      }
      return null;
    }
  };

  // Check if we're adding to a specific project
  const targetProjectId = new URLSearchParams(window.location.search).get('addToProject');
  const targetProject = useQuery(
    api.projects.getProject,
    targetProjectId ? { projectId: targetProjectId as Id<'projects'> } : 'skip'
  );

  return (
    <ReferenceSelectionProvider>
      <div className={`min-h-screen ${className}`}>

      {/* Project Context Banner */}
      {targetProject && (
        <div className="bg-yellow-300 border-b-4 border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <div className="text-sm font-medium text-gray-900">Adding references to:</div>
                  <div className="text-lg font-bold text-gray-900">{targetProject.name}</div>
                </div>
              </div>
              <button
                onClick={() => navigate(`/projects/${targetProjectId}`)}
                className="btn btn-sm bg-gray-900 text-white hover:bg-gray-800"
              >
                Back to Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
        {/* New SimpleFilterBar - positioned above results */}
        <div className="mb-4 sm:mb-6">
          <SimpleFilterBar
            availableTags={availableTagsForFilter}
            showSearch={true}
            showLegend={true}
            searchPlaceholder="Search references..."
            onTagSelect={handleNewTagSelect}
            showViewModeToggle={true}
            compact={false}
            className="card bg-base-100 border-2 border-base-300 p-3 sm:p-4"
          />
        </div>

        {/* Results Count */}
        <div className="mb-3 sm:mb-4 text-sm text-base-content/70 px-1">
          {finalFilteredReferences.length === 0 ? (
            'No references found'
          ) : (
            `${finalFilteredReferences.length} reference${finalFilteredReferences.length === 1 ? '' : 's'} found`
          )}
        </div>

        {/* Empty State */}
        {finalFilteredReferences.length === 0 && (
          <div className="card bg-base-100 shadow-lg border-2 border-base-300 mx-1 sm:mx-0">
            <div className="card-body text-center py-8 sm:py-12">
              <div className="text-base-content/40 mb-4">
                <svg className="mx-auto h-10 sm:h-12 w-10 sm:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-base-content mb-2">No references found</h3>
              <p className="text-sm text-base-content/70 mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your search filters or adding new references.'
                  : 'Start by adding some references to see them here.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  window.location.assign('/new-reference');
                }}
                className="btn bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400 text-sm sm:text-base"
              >
                Add Reference
              </button>
            </div>
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && finalFilteredReferences.length > 0 && (
          <GridView
            references={finalFilteredReferences}
            onEditReference={onEditReference}
            onDelete={handleDelete}
            onAIPrompt={handleAIPromptClick}
            onTagClick={handleTagClick}
            generateReferenceUrl={generateReferenceUrl}
          />
        )}

        {/* Masonry View */}
        {viewMode === 'masonry' && finalFilteredReferences.length > 0 && (
          <MasonryView
            references={finalFilteredReferences}
            onEditReference={onEditReference}
            onDelete={handleDelete}
            onAIPrompt={handleAIPromptClick}
            onTagClick={handleTagClick}
            generateReferenceUrl={generateReferenceUrl}
          />
        )}

        {/* List View */}
        {viewMode === 'list' && finalFilteredReferences.length > 0 && (
          <ListView
            references={finalFilteredReferences}
            onEditReference={onEditReference}
            onDelete={handleDelete}
            onAIPrompt={handleAIPromptClick}
            onTagClick={handleTagClick}
            generateReferenceUrl={generateReferenceUrl}
          />
        )}

        {/* Infinite scroll sentinel for paginated queries */}
        {isPaginated && paginatedResult && (
          <InfiniteScrollSentinel
            loadMore={paginatedResult.loadMore}
            status={paginatedResult.status}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingReference && (
        <div className="modal modal-open">
          <div className="modal-box">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <title>Warning icon</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-bold text-base-content">Delete Reference</h3>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-base-content/70">
                Are you sure you want to delete "{deletingReference.title}"? This action cannot be undone.
              </p>
            </div>
            
            <div className="modal-action">
              <button
                type="button"
                onClick={cancelDelete}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void confirmDelete(); }}
                className="btn btn-error"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Prompt Generator Modal */}
      {aiPromptReference && (
        <AIPromptGeneratorModal
          reference={aiPromptReference}
          isOpen={isAIPromptModalOpen}
          onClose={() => {
            setIsAIPromptModalOpen(false);
            setAiPromptReference(null);
          }}
          onGeneratePrompt={handleGenerateAIPrompt}
        />
      )}

      {/* Selection Action Bar */}
      <SelectionActionBar />
    </div>
    </ReferenceSelectionProvider>
  );
}

